import { CircularProgress } from '@mui/material';
import { distributorsContext } from 'pages/distributorManagement/distributorManagementResolver';
import { AccountNumberParams } from 'pages/managePricing/managePricingResolver';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { ROUTES } from 'routing';
import { NoPropsJustChildren, TableDataContext } from 'shared/types';
import { DistributorsData } from 'types/backend';

type DistributorContext = { matchingCustomer: DistributorsData; isLoading: boolean };

const distributorContext = createContext<DistributorContext>({
    isLoading: false,
} as DistributorContext);

export function ProvideDistributor({ children }: NoPropsJustChildren) {
    const params = useParams<AccountNumberParams>();
    const distributorsData = useContext<TableDataContext<DistributorsData>>(distributorsContext);
    const distributorRows: DistributorsData[] = distributorsData.response?.data?.rows || [];
    const matchingCustomer = distributorRows.find((row) => row.customerNumber === params.accountNumber);
    const isFilteredForDistributor = distributorsData.filtersApplied?.items?.[0].value === params.accountNumber;

    useEffect(() => {
        if (!matchingCustomer && !isFilteredForDistributor) {
            distributorsData.dontDebounceTableDataRequest({
                FilterModel: {
                    items: [{ columnField: 'customerNumber', operatorValue: 'equals', value: params.accountNumber }],
                },
            });
        }
    }, [matchingCustomer, params.accountNumber, distributorsData, isFilteredForDistributor]);

    const isLoading = !!(distributorsData.response?.isLoading || distributorsData.response?.isFetching);

    const context = useMemo(() => ({ isLoading, matchingCustomer }), [isLoading, matchingCustomer]);

    if (isLoading || (!matchingCustomer && !isFilteredForDistributor)) return <CircularProgress />;
    if (!matchingCustomer) return <Redirect to={ROUTES.distributorManagement} />;

    return <distributorContext.Provider value={context as DistributorContext}>{children}</distributorContext.Provider>;
}

export function useDistributor() {
    return useContext(distributorContext);
}
